// Connects the editor to the product page: export runs only on click of
// "Add to cart" (not continuously after every piece - saves data/perf on
// mobile), uploads to the Cloudflare Worker (R2), and the exportId is attached
// as a hidden line item property on the cart item (survives through to the
// orders/paid webhook in Phase 3).

(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function getVariantIdInputEl(formId) {
    const nested = document.querySelector(`#${formId} [name="id"]`);
    if (nested) return nested;
    return document.querySelector(`[form="${formId}"][name="id"]`);
  }

  function getSelectedVariantId(formId) {
    const input = getVariantIdInputEl(formId);
    return input ? input.value : null;
  }

  // "Unframed" contains "framed" as a substring, so it's excluded first -
  // exact title wording confirmed live (Solo Cat Portrait): "Framed Print – …",
  // "Unframed Print – …", "Digital File Only".
  function isFramedVariantTitle(title) {
    const t = (title || '').toLowerCase();
    if (t.includes('unframed')) return false;
    return t.includes('framed');
  }

  // English, not Slovak like the rest of the editor (intentional) - the page
  // is primarily EN (with DE/FR translation via Shopify Translate & Adapt). The
  // rest of the editor's text isn't hooked into the locale-key system at all
  // yet (full localization is a separate final task before launch) - these
  // messages are intentionally hardcoded in English rather than via
  // "{{ 'x' | t }}", so they aren't a lone exception in the middle of an
  // otherwise unaddressed remainder.
  function progressCounterMessage(filled, total) {
    const word = total === 1 ? 'photo' : 'photos';
    return `${filled} / ${total} ${word} added`;
  }

  // Instead of a separate completeness-gate message elsewhere on the page
  // (previously ".se-status" on click of "Add to cart"), the same
  // "se-progress-counter" element is simply highlighted and given a short
  // addendum - one message in one place, not two different texts alternating
  // at different moments.
  function progressCounterWarningMessage(filled, total) {
    return `${progressCounterMessage(filled, total)} — fill in the rest to continue`;
  }

  // "variant" optional - "thumb" uploads a small cart/cart-drawer/cart-notification
  // thumbnail (separate file, see shapemory-export.js and the Worker) instead
  // of the full print-quality version.
  async function uploadWithRetry(workerUrl, id, blob, variant, attempts = 3) {
    const qs = variant ? `?id=${id}&variant=${variant}` : `?id=${id}`;
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try {
        const res = await fetch(`${workerUrl}/api/upload${qs}`, {
          method: 'POST',
          headers: { 'Content-Type': 'image/png' },
          body: blob,
        });
        if (!res.ok) throw new Error('upload failed (' + res.status + ')');
        return;
      } catch (err) {
        lastErr = err;
        if (i < attempts - 1) await new Promise((r) => setTimeout(r, 1000 * (i + 1)));
      }
    }
    throw lastErr;
  }

  // Classifies the title of a single Shopify "Style & Size" option value
  // (e.g. "Framed Print – A3 (11.7 x 16.5 in)") into a { style, size } pair,
  // used by initStyleSizePicker() below. Shopify has just ONE option with 7
  // combined values for this product (confirmed via GraphQL), not two
  // separate options - this is purely client-side parsing of existing
  // strings, it changes nothing on the Shopify side.
  function classifyVariantTitle(title) {
    const t = (title || '').toLowerCase();
    let style = null;
    if (t.includes('digital')) style = 'digital';
    else if (t.includes('unframed')) style = 'unframed';
    else if (t.includes('framed')) style = 'framed';

    let size = null;
    if (t.includes('a3')) size = 'a3';
    else if (t.includes('50 x 70') || t.includes('50x70')) size = '5070';
    else if (t.includes('a1')) size = 'a1';

    return { style, size };
  }

  const STYLE_TEXT = { framed: 'Framed', unframed: 'Unframed', digital: 'Digital File Only' };
  const SIZE_TEXT = { a3: 'A3', 5070: '50×70cm', a1: 'A1' };

  // Export pixel width per physical print size (2026-08-19) - a Gelato print
  // job flagged "Medium 205 dpi" on a downloaded export, traced back to
  // exportCollageAsPng() always being called with a hardcoded 3000px
  // regardless of which size the customer actually bought. A3 and 50×70cm
  // here hit their exact 300dpi width (short side: 29.7cm/11.69in and
  // 50cm/19.69in respectively, ×300). A1's true 300dpi width would be
  // 7016px (23.39in × 300) - deliberately capped lower instead: a live
  // browser test at the full A1 size measured a ~266MB canvas buffer, ~3s
  // draw + ~8s PNG encode on a fast desktop alone (see project notes), and
  // that's without a real photo-decode workload or the sequential thumbnail
  // export that follows it. Mobile Safari/Chrome have much tighter
  // per-tab memory ceilings than desktop and no way to verify from this
  // environment - 6000px keeps some of that safety margin (still a big
  // jump from 3000px, ~230dpi instead of ~128dpi) until it's been tried on
  // a real phone.
  //
  // "digital" (2026-08-21): explicitly capped at the same 6000px as A1,
  // rather than falling back to EXPORT_WIDTH_FALLBACK's 3000px - a
  // "high-res" digital file should be at least as good as the largest print
  // size sold, not worse than the smallest one (A3, 3508px). Only a
  // genuinely unrecognized/missing variant title still falls back to
  // EXPORT_WIDTH_FALLBACK.
  const PRINT_EXPORT_WIDTH = { a3: 3508, 5070: 5906, a1: 6000 };
  const DIGITAL_EXPORT_WIDTH = PRINT_EXPORT_WIDTH.a1;
  const EXPORT_WIDTH_FALLBACK = 3000;

  function getExportWidth(variantTitle) {
    const { style, size } = classifyVariantTitle(variantTitle);
    if (style === 'digital') return DIGITAL_EXPORT_WIDTH;
    return PRINT_EXPORT_WIDTH[size] || EXPORT_WIDTH_FALLBACK;
  }

  // Compact Style + Size picker: finds the hidden native Dawn radio inputs
  // (one option "Style & Size", 7 values), classifies them, and on click
  // of one of our pills programmatically checks the matching radio + fires
  // a "change" event on it - this triggers the entire existing Dawn
  // variant-switching mechanism (<variant-selects>), including price
  // respecting the current currency/Shopify Markets, availability, and
  // updating the hidden "[name=id]" input - none of that needs
  // reimplementing.
  //
  // IMPORTANT: Dawn redraws/replaces the native <variant-selects> radio
  // inputs (and the price block) with fresh DOM from an Ajax
  // section-rendering fetch after EVERY variant change (confirmed live -
  // old references to them silently stop working after the first change).
  // So nothing is cached across clicks here - everything (the radios, and
  // the .se-style-size-picker itself and its buttons) is looked up fresh on
  // every interaction via "document", and clicks are listened for via
  // delegation on "document", not with direct listeners on individual
  // buttons.
  function initStyleSizePicker() {
    if (!document.querySelector('.se-style-size-picker')) return;

    function currentRadios() {
      return Array.from(document.querySelectorAll('variant-selects input[type="radio"]'));
    }

    function findRadio(style, size) {
      const targetKey = style + '|' + (style === 'digital' ? '' : size || '');
      return (
        currentRadios().find((radio) => {
          const c = classifyVariantTitle(radio.value);
          return c.style && c.style + '|' + (c.size || '') === targetKey;
        }) || null
      );
    }

    // Dawn's own Ajax variant-switching code focuses the freshly re-rendered
    // native radio input after replacing it (an accessibility pattern, so
    // screen readers announce the new selection) - by default that focus()
    // call scrolls the page to wherever that (invisible,
    // position:absolute) input's box resolves to, which on this reordered
    // mobile layout throws the whole page to the very top (confirmed live:
    // scrollY jumps to 0 and document.activeElement becomes the native
    // radio a few hundred ms after the "change" event). We don't own
    // Dawn's code to fix the focus() call at its source, so instead we
    // hold the scroll position steady for a short window right after WE
    // trigger a variant change: temporarily patch focus() to pass
    // "preventScroll: true" (covers the common case), and as a safety net
    // also snap scrollY back only if it lands suspiciously close to the
    // top - never touching scroll positions the user reaches themselves.
    function selectVariant(style, size) {
      const radio = findRadio(style, size);
      if (!radio) return;

      const savedScrollY = window.scrollY;
      const nativeFocus = HTMLElement.prototype.focus;
      HTMLElement.prototype.focus = function (opts) {
        return nativeFocus.call(this, Object.assign({}, opts, { preventScroll: true }));
      };

      function onScroll() {
        if (Math.abs(window.scrollY - savedScrollY) > 40 && window.scrollY < 40) {
          window.scrollTo(0, savedScrollY);
        }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      setTimeout(() => {
        window.removeEventListener('scroll', onScroll);
        HTMLElement.prototype.focus = nativeFocus;
      }, 3000);

      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Reads the CURRENT state (which native radio is checked) and projects
    // it onto every ".se-style-size-picker" instance currently present on
    // the page (normally one) - called on every interaction and after
    // every variant change (including ones Dawn triggers itself), so it
    // never relies on element references held across async steps.
    function syncVisualState() {
      const checked = currentRadios().find((r) => r.checked);
      if (!checked) return;
      const { style, size } = classifyVariantTitle(checked.value);
      document.querySelectorAll('.se-style-size-picker').forEach((picker) => {
        const styleBtns = picker.querySelectorAll('[data-style]');
        const sizeBtns = picker.querySelectorAll('[data-size]');
        const sizeRow = picker.querySelector('[data-role="size"]');
        const sizeLabel = picker.querySelector('[data-role="size-label"]');
        const caption = picker.querySelector('.se-style-size-caption');
        styleBtns.forEach((b) => b.classList.toggle('se-is-selected', b.dataset.style === style));
        sizeBtns.forEach((b) => b.classList.toggle('se-is-selected', b.dataset.size === size));
        const isDigital = style === 'digital';
        if (sizeRow) sizeRow.hidden = isDigital;
        if (sizeLabel) sizeLabel.hidden = isDigital;
        if (caption) {
          if (isDigital) caption.textContent = STYLE_TEXT.digital;
          else if (style && size) caption.textContent = `${STYLE_TEXT[style]} · ${SIZE_TEXT[size]}`;
          else caption.textContent = '';
        }
      });
    }

    document.addEventListener('click', (e) => {
      const styleBtn = e.target.closest('.se-style-size-pill[data-style]');
      if (styleBtn) {
        const style = styleBtn.dataset.style;
        if (style === 'digital') {
          selectVariant('digital', null);
          return;
        }
        const checked = currentRadios().find((r) => r.checked);
        const current = checked ? classifyVariantTitle(checked.value) : {};
        const size = current.size || 'a3';
        selectVariant(style, size);
        return;
      }
      const sizeBtn = e.target.closest('.se-style-size-pill[data-size]');
      if (sizeBtn) {
        const size = sizeBtn.dataset.size;
        const checked = currentRadios().find((r) => r.checked);
        const current = checked ? classifyVariantTitle(checked.value) : {};
        const style = current.style && current.style !== 'digital' ? current.style : 'framed';
        selectVariant(style, size);
      }
    });

    document.addEventListener('change', (e) => {
      if (e.target.matches('variant-selects input[type="radio"]')) syncVisualState();
    });

    syncVisualState();
  }

  // Mobile sticky bottom bar (price + progress counter/CTA) - stays in sync
  // with the real sources of truth (the price block, the "Add to cart"
  // button), instead of duplicating their logic. Clicking the CTA in the
  // bar just forwards the click to the real button - there's no separate
  // cart/export logic here.
  //
  // Dawn redraws/replaces the price block after a variant change (same
  // reason as the native radio inputs in initStyleSizePicker) - the
  // MutationObserver therefore watches a STABLE ancestor
  // (".product__info-container", which itself is never replaced) and, on
  // every change within it, looks up the price element again via
  // "document.querySelector" rather than from a stored reference.
  function wireStickyBar() {
    const bar = document.querySelector('.se-sticky-bar');
    if (!bar) return;

    // Moved directly into <body> so "position: fixed" is guaranteed to
    // work against the full viewport regardless of whether some ancestor
    // (e.g. scroll-trigger animations on product__info-wrapper) sets a
    // transform - same reason as the piece-edit modal in core.js.
    document.body.appendChild(bar);

    const priceEl = bar.querySelector('.se-sticky-bar-price');
    const ctaBtn = bar.querySelector('.se-sticky-bar-cta');

    function syncPrice() {
      const realPriceEl = document.querySelector('[id^="price-"]');
      if (!realPriceEl || !priceEl) return;
      // "innerText" (not "textContent") - the price block also contains
      // hidden variants (sale/sold out/etc.) of which CSS shows only one;
      // "textContent" would concatenate all of them into one confusing
      // string.
      priceEl.textContent = realPriceEl.innerText.trim().replace(/\s+/g, ' ');
    }

    function syncCta() {
      const realAddToCartBtn = document.querySelector('.se-add-to-cart-btn');
      if (!realAddToCartBtn || !ctaBtn) return;
      // ".se-status" (and its "se-is-busy" spinner, see initEditorBlock) is
      // hidden on mobile - the sticky bar's CTA is the only visible button
      // there, so the busy state has to be mirrored onto IT instead, or a
      // customer on a slow phone taps "Add to cart", sees nothing change for
      // several seconds, and has no way to tell the tap even registered
      // (2026-08-21 fix). Read directly off the live ".se-status" element
      // rather than caching it - same reasoning as the rest of this
      // function, nothing here is safe to hold onto across the async gap.
      const statusEl = document.querySelector('.se-status');
      const busy = !!(statusEl && statusEl.classList.contains('se-is-busy'));
      ctaBtn.classList.toggle('se-is-busy', busy);
      ctaBtn.textContent = busy ? statusEl.textContent : realAddToCartBtn.textContent;
      ctaBtn.disabled = realAddToCartBtn.disabled;
    }

    syncPrice();
    syncCta();

    const infoContainer = document.querySelector('.product__info-container') || document.body;
    new MutationObserver(() => {
      syncPrice();
      syncCta();
    }).observe(infoContainer, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });

    ctaBtn.addEventListener('click', () => {
      const realAddToCartBtn = document.querySelector('.se-add-to-cart-btn');
      if (realAddToCartBtn) realAddToCartBtn.click();
    });

    bar.hidden = false;
  }

  // The editor now renders in TWO places in the DOM at once (left column:
  // stage/gallery/lightbox/sticky bar; right column: grayscale/add-to-cart/
  // status) - see the comment in shapemory-editor.liquid. Since there is
  // always exactly one editor instance on a real product page (the same
  // assumption the ShapemoryEditor singleton in core.js already relies on),
  // element lookups are no longer scoped to one shared "root" wrapper but
  // go straight through "document".
  async function initEditorBlock() {
    const stage = document.querySelector('.se-stage-container');
    if (!stage) return;

    const grayscaleBtn = document.querySelector('.se-grayscale-btn');
    const addToCartBtn = document.querySelector('.se-add-to-cart-btn');
    const status = document.querySelector('.se-status');
    const progressCounter = document.querySelector('.se-progress-counter');
    const previewWrap = document.querySelector('.se-export-preview');
    const previewLabel = document.querySelector('.se-export-preview-label');
    const previewImg = document.querySelector('.se-export-preview-img');

    // Defensive-only (2026-08-19): reported once, in incognito, after
    // several "Update preview" clicks - the blob: URL failed to decode and
    // the browser fell back to its broken-image icon. Never reproduced since
    // (not incognito-specific either), so this isn't chasing the root
    // cause - just making sure a future recurrence degrades gracefully
    // instead of showing a broken icon. One silent reload attempt (a fresh
    // "error" on the exact same blob: URL is occasionally just a transient
    // decode hiccup, not a genuinely unusable blob), then a plain message
    // in place of the image if that fails too. Reset on every new
    // successful export below, so a stale failure never lingers across a
    // "Update preview" click that actually worked.
    let previewErrorRetried = false;
    previewImg.addEventListener('error', () => {
      if (!previewImg.src) return; // our own "clear src" below re-triggers "error" harmlessly
      if (!previewErrorRetried) {
        previewErrorRetried = true;
        const src = previewImg.src;
        previewImg.src = '';
        previewImg.src = src;
        return;
      }
      previewImg.style.display = 'none';
      previewLabel.textContent = "Couldn't load the preview image (your collage itself is fine - this is just a display glitch). Click the button again for a fresh attempt.";
    });

    const variantsJsonEl = document.querySelector('.se-variants-json');
    const editorRoot = document.querySelector('.shapemory-editor');

    const workerUrl = editorRoot.dataset.workerUrl;
    const svgUrl = editorRoot.dataset.svgUrl;
    const formId = editorRoot.dataset.productFormId;
    let grayscaleOn = false;

    // Sticky bar counter/CTA toggling - wired to the same completion data
    // as the inline progress counter below (one source of truth).
    const stickyBarEl = document.querySelector('.se-sticky-bar');
    const stickyBarCounter = stickyBarEl ? stickyBarEl.querySelector('.se-sticky-bar-counter') : null;

    function updateStickyBarState(completion) {
      if (!stickyBarEl) return;
      if (stickyBarCounter) {
        stickyBarCounter.textContent = progressCounterMessage(completion.filled, completion.total);
      }
      stickyBarEl.dataset.state = completion.missing > 0 ? 'counting' : 'complete';
    }

    // The black frame around the preview should only show for "Framed"
    // variants, and react live to a variant change at any time (not only
    // when the preview is first opened) - product-info.js (theme) sets
    // this same hidden "[name=id]" input's value on every variant change
    // and fires a "change" event on it (see updateVariantInputs() in
    // assets/product-info.js), so it's enough to listen for that, no
    // custom polling needed.
    let variantTitleById = {};
    try {
      (JSON.parse(variantsJsonEl?.textContent || '[]')).forEach((v) => {
        variantTitleById[String(v.id)] = v.title;
      });
    } catch (err) {
      console.error('Failed to parse variant titles (frame will not display):', err);
    }

    function updateFrameVisibility() {
      const variantId = getSelectedVariantId(formId);
      const title = variantTitleById[String(variantId)];
      previewImg.classList.toggle('se-is-framed', isFramedVariantTitle(title));
    }

    updateFrameVisibility();
    const variantIdInputEl = getVariantIdInputEl(formId);
    if (variantIdInputEl) variantIdInputEl.addEventListener('change', updateFrameVisibility);

    const info = await ShapemoryEditor.init(stage, svgUrl);
    console.log(`Shapemory editor ready: ${info.pieceCount} pieces, ${info.fixedCount} fixed details.`);

    // Running counter of filled pieces, visible throughout the whole
    // collage assembly (not just when attempting to add to cart, like
    // "status" above). "showCounterWarning" switches the same element into
    // the highlighted completeness-gate form (see addToCartBtn below) -
    // every subsequent photo added always reverts it back to the normal
    // form, since that's new progress information.
    function showCounterNormal(completion) {
      if (progressCounter) {
        progressCounter.textContent = progressCounterMessage(completion.filled, completion.total);
        progressCounter.classList.remove('se-is-warning');
      }
      updateStickyBarState(completion);
    }

    function showCounterWarning(completion) {
      if (progressCounter) {
        progressCounter.textContent = progressCounterWarningMessage(completion.filled, completion.total);
        progressCounter.classList.add('se-is-warning');
      }
      updateStickyBarState(completion);
    }

    showCounterNormal({ filled: 0, total: info.pieceCount, missing: info.pieceCount });
    ShapemoryEditor.setProgressListener((completion) => showCounterNormal(completion));

    // Bug fix (2026-08-19): a preview generated by the first "Add to cart"
    // click was being uploaded unchanged by the second ("Confirm…") click
    // even if the customer went back and edited a piece, or toggled
    // grayscale, in between - the stale pre-edit version silently ended up
    // in the cart. Any change that would affect the exported pixels must
    // throw away that cached preview so the next click re-exports from the
    // current state. A no-op (guarded by "if (!pendingExport)") before any
    // preview has ever been generated - the first-time flow is unaffected.
    //
    // The visible preview IMAGE is deliberately left alone here (not
    // hidden) - only the button label and the cached blob are reset.
    // Hiding it too was tried first and reported back as a regression: it
    // made the whole mockup visually vanish on every small edit (even just
    // toggling grayscale), which reads as broken rather than "needs
    // re-confirming". Leaving the (now slightly stale) image up until the
    // next click replaces it is the better tradeoff - but that silent
    // staleness turned out to be its own problem (reported back too): with
    // no visual cue, a customer has no way to notice the image on screen no
    // longer matches their last edit. "se-is-stale" on the wrapper drives
    // both a dimmed image and a "Preview outdated" badge over it (pure CSS,
    // see shapemory-editor.css), and the button label changes from the
    // generic "Add to cart" to "Update preview" - reached only from here,
    // never on the very first click, since this function is always a no-op
    // until a preview has actually been generated once (the guard above).
    function invalidatePendingExport() {
      if (!pendingExport) return;
      pendingExport = null;
      addToCartBtn.textContent = 'Update preview';
      previewWrap.classList.add('se-is-stale');
      status.classList.remove('se-is-busy');
      status.textContent = '';
    }
    ShapemoryEditor.setChangeListener(invalidatePendingExport);

    grayscaleBtn.addEventListener('click', () => {
      grayscaleOn = !grayscaleOn;
      // Toggled on <body>, not on "root" - the piece-edit modal lives
      // outside the block's root (appended directly to <body>), so a class
      // scoped to "root" wouldn't reach it.
      document.body.classList.toggle('se-is-grayscale', grayscaleOn);
      grayscaleBtn.textContent = grayscaleOn ? 'Color' : 'Black & White';
      invalidatePendingExport();
    });

    // Supporting/trust product photo gallery - clicking a thumbnail opens
    // the same photo in a larger lightbox (data-full-src from the liquid
    // "image_url" filter, a real Shopify CDN URL, not a shrunk copy).
    // Nothing else on the page changes from this interaction - the gallery
    // doesn't replace the editor.
    const galleryThumbs = Array.from(document.querySelectorAll('.se-gallery-thumb'));
    const lightbox = document.querySelector('.se-lightbox');
    const lightboxImg = lightbox ? lightbox.querySelector('.se-lightbox-img') : null;
    const lightboxCloseBtn = lightbox ? lightbox.querySelector('.se-lightbox-close') : null;

    function openLightbox(src, alt) {
      if (!lightbox || !lightboxImg) return;
      lightboxImg.src = src;
      lightboxImg.alt = alt || '';
      lightbox.classList.add('se-is-open');
      lightbox.setAttribute('aria-hidden', 'false');
    }

    function closeLightbox() {
      if (!lightbox || !lightboxImg) return;
      lightbox.classList.remove('se-is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      lightboxImg.src = '';
    }

    galleryThumbs.forEach((btn) => {
      btn.addEventListener('click', () => {
        const img = btn.querySelector('img');
        openLightbox(btn.dataset.fullSrc, img ? img.alt : '');
      });
    });
    if (lightboxCloseBtn) lightboxCloseBtn.addEventListener('click', closeLightbox);
    if (lightbox) {
      lightbox.addEventListener('click', (e) => {
        if (e.target === lightbox) closeLightbox();
      });
    }
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeLightbox();
    });

    // TEMPORARY (empty-export diagnostics): the button now first just
    // exports and shows a preview, without going straight to upload/cart -
    // this gives room to properly look at/zoom into/save the preview in
    // Safari before anything is sent. A second click on the (renamed)
    // button then uploads and adds to cart using that same, already-
    // exported file.
    let pendingExport = null;

    addToCartBtn.addEventListener('click', async () => {
      // Applies equally to all variants (physical and digital) - checked
      // on every click (even on "Confirm..."), not just the first, as a
      // cheap extra safeguard, even though the editor currently has no way
      // to empty a piece again once it has received a photo.
      const completion = ShapemoryEditor.getCompletionStatus();
      if (completion.missing > 0) {
        showCounterWarning(completion);
        if (progressCounter) progressCounter.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }

      if (!pendingExport) {
        addToCartBtn.disabled = true;
        // "se-is-busy" drives the spinner (shapemory-editor.css) - the local
        // canvas render below (exportCollageAsPng, twice: full-size then
        // thumbnail) is synchronous-ish CPU work that can take a few seconds
        // on slower devices with nothing else on screen changing, which
        // previously read as a frozen page (2026-08-21 fix). Cleared as soon
        // as either the success or error status text below replaces this
        // one - never left showing past that.
        status.textContent = 'Generating preview…';
        status.classList.add('se-is-busy');
        try {
          const model = ShapemoryEditor.getExportModel();
          const exportWidth = getExportWidth(variantTitleById[String(getSelectedVariantId(formId))]);
          const exported = await exportCollageAsPng(model, exportWidth, grayscaleOn);
          // Small (300px) thumbnail for cart/cart-drawer/cart-notification -
          // the same width the theme already requests via
          // "image_url: width: 300" for the original product photo.
          // Computed here (not again on the second click), since the
          // pieces don't change between steps.
          const thumb = await exportCollageAsPng(model, 300, grayscaleOn);
          exported.thumbBlob = thumb.blob;
          pendingExport = exported;
          previewErrorRetried = false;
          previewImg.style.display = '';
          previewImg.src = exported.url;
          previewLabel.textContent = `Preview of your collage (${exported.width}×${exported.height}px) - check that the photos are visible:`;
          previewWrap.hidden = false;
          previewWrap.classList.remove('se-is-stale');
          previewWrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          status.classList.remove('se-is-busy');
          status.textContent = 'Check the preview above and confirm to continue.';
          addToCartBtn.textContent = 'Confirm and add to cart';
        } catch (err) {
          status.classList.remove('se-is-busy');
          status.textContent = 'Export failed: ' + err.message;
        } finally {
          addToCartBtn.disabled = false;
        }
        return;
      }

      addToCartBtn.disabled = true;
      try {
        const exportId = crypto.randomUUID();
        status.textContent = 'Uploading to server…';
        await uploadWithRetry(workerUrl, exportId, pendingExport.blob);
        // The thumbnail is uploaded alongside the full version under the
        // same exportId - if this fails, it won't block adding to cart
        // (both the webhook and the cart JS fall back to the original
        // product photo when it's missing).
        try {
          await uploadWithRetry(workerUrl, exportId, pendingExport.thumbBlob, 'thumb');
        } catch (thumbErr) {
          console.error('Failed to upload cart thumbnail (non-critical):', thumbErr);
        }

        const variantId = getSelectedVariantId(formId);
        if (!variantId) throw new Error('could not determine the selected variant');

        status.textContent = 'Adding to cart…';
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties: { _shapemory_export_id: exportId } }],
          }),
        });
        if (!res.ok) throw new Error('add to cart failed (' + res.status + ')');

        const cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        window.location.href = cartRoot + 'cart';
      } catch (err) {
        status.textContent = 'Could not finish adding to cart. Please try again, or contact us if the problem persists.';
        addToCartBtn.disabled = false;
      }
    });
  }

  ready(() => {
    if (document.querySelector('.shapemory-editor')) initEditorBlock();
    initStyleSizePicker();
    wireStickyBar();
  });
})();
